import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BaseService } from '../base.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { UserAdminDetailsInfos } from './models/user-admin-details-infos.model';
import { UserAdminUI } from "@app/core/services/administrator/models/user-admin.model";
import { HttpUtilService } from "@services/http.util.service";
import { KeycloakService } from 'keycloak-angular';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Page } from '../commons/page';
import { RoleService } from './role.service';
import { Role } from './models/role.model';
import { Router } from '@angular/router';
import { NotificationService } from '../util/notification.service';

@Injectable({
  providedIn: 'root'
})
export class AdministratorService extends BaseService {
  loggedAgent$ = new BehaviorSubject<UserAdminUI>(null);

  constructor(
    private httpClient: HttpClient,
    protected notificationService: NotificationService,
    protected translate: TranslateService,
    private httpUtilService: HttpUtilService,
    private keycloakService: KeycloakService,
    private roleService: RoleService,
    private router: Router
  ) {
    super(notificationService, translate);
  }

  public refreshLoggedAgent(): Observable<UserAdminUI> {
    const keycloakId = this.keycloakService.getKeycloakInstance().subject;
    return this.httpClient.get<UserAdminUI>(environment.api.mobilityUrl + '/v1/realms/' + environment.keycloak.realm + '/reference/' + keycloakId + '/user')
      .pipe(
        switchMap(user => {
          const roles = this.roleService.rolesInformations$.getValue();
          user.roleInfos = roles.find(r => r.name === user.role);

          if (user.roleInfos) {
            return of(user);
          }

          return this.httpClient.get<Role>(`${environment.api.mobilityUrl}/api/user-manager/v1/roles/${user.role}`).pipe(
            map(role => {
              user.roleInfos = role;
              return user;
            }),
            catchError(err => {
              if (err.status === 404) {
                console.error(err);
                this.router.navigate(['/error/404']);
                throw new Error(`Role with name ${user.role} doesn't exists`);
              }
              this.router.navigate(['/error/500']);
              throw new Error('Unknow error');
            })
          )
        }),
        tap(user => {
          this.loggedAgent$.next(user);
        })
      );
  }

  public createUserBack(administrator: UserAdminUI): Observable<any> {
    return this.httpClient.post<any>(`${environment.api.mobilityUrl}/v1/realms/${environment.keycloak.realm}/clients/${environment.keycloak.clientId}/users`, administrator);
  }

  public updateUserBack(administrator: UserAdminUI, adminId: number): Observable<void> {
    return this.httpClient.put<void>(`${environment.api.mobilityUrl}/v1/users/${adminId}`, administrator);
  }

  public updateUserBackDetails(administrator: UserAdminDetailsInfos, adminId: number): Observable<UserAdminDetailsInfos> {
    return this.httpClient.put<UserAdminDetailsInfos>(`${environment.api.mobilityUrl}/v1/users/${adminId}/details`, administrator);
  }

  public getAdministratorByReference(reference: string): Observable<UserAdminUI> {
    return this.httpClient.get<UserAdminUI>(`${environment.api.mobilityUrl}/v1/realms/${environment.keycloak.realm}/reference/${reference}/user`);
  }

  public getAdministratorByKCId(kcId: string): Observable<UserAdminUI> {
    return this.httpClient.get<UserAdminUI>(`${environment.api.mobilityUrl}/v1/realms/${environment.keycloak.realm}/reference/${kcId}/user`);
  }

  public getAdministrators(filter, sortOrder = 'asc', sortElement = '', pageNumber = 0, pageSize = 10): Observable<Page<UserAdminUI>> {

    let params = new HttpParams()
    .set('page', pageNumber.toString())
    .set('size', pageSize.toString());

    if (sortElement && sortOrder) {
      params = params.set('sort', this.httpUtilService.getSortElement(sortElement, {
        'EMAIL': 'userDetails.userEmailAddress',
        'LAST_NAME': 'userDetails.userLastName',
        'FIRST_NAME': 'userDetails.userFirstName'
      }) + ',' + sortOrder);
    }

    if (filter?.email) {
      params = params.set('email', filter.email);
    }

    if (filter?.firstName) {
      params = params.set('firstName', filter.firstName);
    }

    if (filter?.lastName) {
      params = params.set('lastName', filter.lastName);
    }

    if (filter?.role) {
      if (Array.isArray(filter.role)) {
        filter.role.forEach(role => {
          params = params.append('role', role);
        });
      } else {
        params = params.append('role', filter.role);
      }
    } else {
      environment.roles.forEach(role => {
        params = params.append('role', role);
      });
    }

    return this.httpClient.get<Page<UserAdminUI>>(`${environment.api.mobilityUrl}/v1/realms/${environment.keycloak.realm}/users`, { params }).pipe(
      map((admins: Page<UserAdminUI>) => {
        if (admins && admins.content) {
          const rolesInfo = this.roleService.rolesInformations$.getValue();
          for (let admin of admins.content) {
            admin.roleInfos = rolesInfo.find(r => admin.role === r.name);
          }
        }

        return admins;
      })
    );
  }

  getAdministratorById(adminId: number): Observable<UserAdminUI> {
    return this.httpClient.get<UserAdminUI>(`${environment.api.mobilityUrl}/v1/users/${adminId}`).pipe(
      switchMap(user => {
        const roles = this.roleService.rolesInformations$.getValue();
        user.roleInfos = roles.find(r => r.name === user.role);

        if (user.roleInfos) {
          return of(user);
        }

        return this.httpClient.get<Role>(`${environment.api.mobilityUrl}/api/user-manager/v1/roles/${user.role}`).pipe(
          map(role => {
            user.roleInfos = role;
            return user;
          }),
          catchError(err => {
            if (err.status === 404) {
              console.error(err);
              this.router.navigate(['/error/404']);
              throw new Error(`Role with name ${user.role} doesn't exists`);
            }
            this.router.navigate(['/error/500']);
            throw new Error('Unknow error');
          })
        )
      })
    );
  }

  findAdministratorByEmail(email: string): Observable<UserAdminUI[]> {
    const params = this.httpUtilService.buildHttpParams({ email });
    return this.httpClient.get<UserAdminUI[]>(`${environment.api.mobilityUrl}/v1/admin/accounts/find`, { params });
  }

  deleteAdministrator(adminId: number): Observable<any> {
    return this.httpClient.delete(`${environment.api.mobilityUrl}/v1/users/${adminId}`);
  }

  updatePassword(userId: number, credentials): Observable<any> {
    return this.httpClient.put(`${environment.api.mobilityUrl}/v1/users/${userId}/credentials`, credentials);
  }
}
